/**
 * PhaseTrail - Trilha de fases (estilo Bitrix) compartilhada entre módulos
 *
 * Faixa horizontal slim, normalmente renderizada entre o header e a barra de
 * abas de um modal. Cada fase é um "chevron" clicável que move o registro para
 * a fase escolhida.
 *
 * Estados visuais:
 *   - concluída (fases anteriores à atual): tom suave da cor da fase + check
 *   - atual: cor sólida da fase, com destaque
 *   - futura: neutra (borda) e clicável
 *   - bloqueada (sem permissão / registro fechado / trava de Workflow): não clicável
 *
 * Camada de apresentação: nenhuma regra de negócio vive aqui. Permissões e
 * travas chegam prontas por props (resolvidas por quem monta o componente).
 *
 * @version 1.0.0
 * RELEASE: 2026-09-03 v1.0.0 - Extraído de DealPhaseTrail para reuso em
 *          Negócios, Tarefas, Retenção e Jurídico.
 */

import React from 'react';
import { Check, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/** Item genérico da trilha (cada módulo mapeia sua fase para este formato) */
export interface PhaseTrailItem {
  id: string;
  name: string;
  /** Cor configurada da fase (hex). Quando ausente usa a cor primária */
  color: string | null;
  /** Ordem de exibição na trilha */
  sortOrder: number;
}

export interface PhaseTrailProps {
  /** Fases já filtradas por visibilidade */
  phases: PhaseTrailItem[];
  /** Fase atual do registro */
  currentPhaseId: string | null | undefined;
  /** Retorna se o usuário pode mover o registro PARA a fase informada */
  canMoveToPhase: (phaseId: string) => boolean;
  /** Motivo do bloqueio (tooltip) quando a fase não é clicável */
  blockedReason?: (phaseId: string) => string | null;
  /** Move o registro para a fase clicada */
  onSelectPhase: (phaseId: string) => void;
  /** Trilha somente leitura (registro fechado ou mutação em andamento) */
  readOnly?: boolean;
  className?: string;
}

/** Cor padrão quando a fase não tem cor configurada */
const COR_PADRAO = 'hsl(var(--primary))';

/**
 * Converte uma cor hexadecimal em rgba com a opacidade informada.
 * Usado apenas para as cores dinâmicas vindas do banco (fases).
 */
function corComOpacidade(cor: string, alpha: number): string {
  const hex = cor.trim();
  if (/^#([0-9a-f]{6})$/i.test(hex)) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // Formatos não-hex (hsl/var) não suportam opacidade direta: devolve a própria cor
  return hex;
}

export function PhaseTrail({
  phases,
  currentPhaseId,
  canMoveToPhase,
  blockedReason,
  onSelectPhase,
  readOnly = false,
  className,
}: PhaseTrailProps) {
  // Ordena por sortOrder preservando a ordem configurada
  const fasesOrdenadas = React.useMemo(
    () => [...(phases ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [phases],
  );

  // Índice da fase atual: define quais fases já foram percorridas
  const indiceAtual = React.useMemo(
    () => fasesOrdenadas.findIndex((f) => f.id === currentPhaseId),
    [fasesOrdenadas, currentPhaseId],
  );

  // Rola a trilha para deixar a fase atual visível ao abrir o modal
  const refAtual = React.useRef<HTMLButtonElement | null>(null);
  React.useEffect(() => {
    refAtual.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [currentPhaseId]);

  if (fasesOrdenadas.length === 0) return null;

  return (
    <TooltipProvider>
      <div className={cn('shrink-0 border-b bg-muted/30', className)}>
        <div className="flex items-center gap-1 overflow-x-auto px-3 py-1.5 md:px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {fasesOrdenadas.map((fase, indice) => {
            const cor = fase.color || COR_PADRAO;
            const isAtual = fase.id === currentPhaseId;
            const isConcluida = indiceAtual >= 0 && indice < indiceAtual;
            const primeira = indice === 0;
            const ultima = indice === fasesOrdenadas.length - 1;

            const permitido = !readOnly && !isAtual && canMoveToPhase(fase.id);
            const motivo = permitido ? null : blockedReason?.(fase.id) ?? null;

            // Recortes em chevron (direção "Pipeline glass")
            const recorte = primeira
              ? 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%)'
              : ultima
                ? 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 10px 50%)'
                : 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%, 10px 50%)';

            // Cores dinâmicas por estado (cor da fase vem do banco)
            const estilo: React.CSSProperties = {
              clipPath: recorte,
              paddingLeft: primeira ? 14 : 20,
              paddingRight: ultima ? 14 : 20,
            };

            if (isAtual) {
              estilo.backgroundColor = cor;
              estilo.color = '#fff';
            } else if (isConcluida) {
              estilo.backgroundColor = corComOpacidade(cor, 0.16);
              estilo.color = cor;
            }

            const botao = (
              <button
                key={fase.id}
                ref={isAtual ? refAtual : undefined}
                type="button"
                aria-current={isAtual ? 'step' : undefined}
                disabled={!permitido}
                onClick={() => permitido && onSelectPhase(fase.id)}
                style={estilo}
                className={cn(
                  'group relative flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap',
                  'text-[10px] font-bold uppercase tracking-wide transition-all',
                  primeira && 'rounded-l-md',
                  ultima && 'rounded-r-md',
                  isAtual && 'z-10 shadow-sm',
                  !isAtual && !isConcluida && 'bg-background text-muted-foreground border border-border',
                  permitido ? 'cursor-pointer hover:brightness-95 hover:text-foreground' : 'cursor-default',
                  !permitido && !isAtual && 'opacity-70',
                )}
              >
                {isConcluida && <Check className="h-3 w-3 shrink-0" />}
                {!permitido && !isAtual && !isConcluida && (
                  <Lock className="h-2.5 w-2.5 shrink-0 opacity-60" />
                )}
                <span className="max-w-[150px] truncate">{fase.name}</span>
              </button>
            );

            // Tooltip: nome completo e/ou motivo do bloqueio
            return (
              <Tooltip key={fase.id}>
                <TooltipTrigger asChild>
                  <span className="inline-flex">{botao}</span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs font-medium">{fase.name}</p>
                  {motivo && <p className="mt-0.5 max-w-[240px] text-xs text-muted-foreground">{motivo}</p>}
                  {permitido && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground">Clique para mover para esta fase</p>
                  )}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}
